import * as React from 'react';
import { ClinicTeamMember } from '@bondvet/types/clinicTeam';
import { graphql } from 'gatsby';
import { ChevronRight } from '@mui/icons-material';
import TeamMemberModal from 'locations/components/TeamMembers/TeamMemberModal';
import useTruncateText from 'core/hooks/useTruncateText';
import ImageWrapper from 'core/components/ImageWrapper';
import * as styles from './BlogAuthor.module.scss';

export type BlogAuthorProps = {
    author: ClinicTeamMember;
};

export const query = graphql`
    fragment BlogAuthor on ClinicTeamMember {
        name
        role
        image {
            mobile: file {
                publicURL
                alt
                childImageSharp {
                    gatsbyImageData(
                        width: 185
                        height: 185
                        transformOptions: { fit: COVER, cropFocus: CENTER }
                        sizes: "calc(100vw - 40px)"
                    )
                }
            }
            desktop: file {
                publicURL
                alt
                childImageSharp {
                    gatsbyImageData(
                        width: 185
                        height: 185
                        transformOptions: { fit: COVER, cropFocus: CENTER }
                    )
                }
            }
        }
    }
`;

function BlogAuthor({ author }: BlogAuthorProps): React.ReactElement {
    const [modalOpen, setModalOpen] = React.useState(false);

    const { ref: descriptionRef } = useTruncateText({
        text: author.longDescription || '',
        lines: 4,
    });

    const toggleModalOpen = React.useCallback(() => {
        setModalOpen((prev) => !prev);
    }, []);

    return (
        <div className={styles.root}>
            <div className={styles.topDivider} />
            <h3 className={styles.title}>About the Author</h3>
            <div className={styles.content}>
                <div>
                    <ImageWrapper
                        className={styles.image}
                        image={author.image}
                        alt={author.name}
                        loading="eager"
                    />
                </div>
                <div className={styles.texts}>
                    <div className={styles.name}>{author.name}</div>
                    <div className={styles.role}>{author.role}</div>
                    <div ref={descriptionRef} className={styles.description}>
                        {author.longDescription}
                    </div>
                    <button
                        className={styles.button}
                        onClick={() => toggleModalOpen()}
                    >
                        Read More
                        <ChevronRight />
                    </button>
                </div>
            </div>
            <TeamMemberModal
                modalOpen={modalOpen}
                onClose={toggleModalOpen}
                employee={author}
            />
        </div>
    );
}

export default BlogAuthor;
